<div class="coi-form-container">
  <!-- Loading Spinners -->
  @if (saving || loadingCoiData) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>{{ saving ? 'Saving COI...' : 'Loading COI data...' }}</p>
    </div>
  }

  <mat-sidenav-container class="sidenav-container">
    <!-- Main Form Content -->
    <mat-sidenav-content class="form-content" [class.with-viewer]="showDocumentViewer">
      
      @if (showAISummary) {
        <mat-card class="ai-results-card" appearance="outlined">
          <mat-card-header>
            <mat-icon class="ai-card-icon">psychology</mat-icon>
            <mat-card-title>AI Analysis Results</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <p class="ai-summary">{{ aiSummary }}</p>
            @if (autoPopulatedFieldsDisplay.length > 0) {
              <div class="auto-populated-section">
                <h4>Auto-Populated Fields</h4>
                @for (field of autoPopulatedFieldsDisplay; track field.label) {
                  <div class="auto-populated-item">
                    <span class="field-label">{{ field.label }}</span>
                    <span class="field-value">{{ field.value }}</span>
                    <span class="confidence-badge" [class.high]="field.confidence >= 0.9" [class.medium]="field.confidence >= 0.7 && field.confidence < 0.9">
                      {{ (field.confidence * 100).toFixed(0) }}%
                    </span>
                  </div>
                }
              </div>
            }
          </mat-card-content>
        </mat-card>
      }

      <!-- Header Section -->
      <div class="header-section">
        <div class="title-section">
          <h1>{{ isEditMode ? 'Edit Certificate of Insurance' : 'New Certificate of Insurance' }}</h1>
          <p class="subtitle">{{ isEditMode ? 'Update COI details' : 'Create a new COI record' }}</p>
          <p class="required-fields-note">Fields marked with <span class="required-indicator">*</span> are required</p>
        </div>
        
        <div class="header-actions">
          <button mat-stroked-button color="accent" (click)="cancelSubmit()">
            <mat-icon>close</mat-icon>
            Cancel
          </button>
          <button mat-raised-button color="primary" 
                  [disabled]="!isFormValid() || saving"
                  (click)="onSubmit()">
            <mat-icon>save</mat-icon>
            {{ isEditMode ? 'Update COI' : 'Save COI' }}
          </button>
        </div>
      </div>

      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        
        <!-- Hero Section: File Upload -->
        <mat-card class="hero-card">
          <mat-card-content>
            <div class="hero-content">
              <div class="upload-section-full">
                @if (currentDocument) {
                  <!-- File exists - show preview with replace option -->
                  <div class="file-preview-card">
                    <div class="file-info">
                      <mat-icon color="primary" class="file-icon">description</mat-icon>
                      <div class="file-details">
                        <span class="file-name">COI Document</span>
                        <span class="file-type">{{ currentDocument.fileType === 'pdf' ? 'PDF File' : 'Word Document' }}</span>
                      </div>
                    </div>
                    
                    <div class="file-actions">
                      <button mat-icon-button (click)="removeFile()" type="button" matTooltip="Remove file">
                        <mat-icon>delete</mat-icon>
                      </button>
                      
                      <input type="file" #fileInput 
                            (change)="onFileSelected($event)"
                            accept=".pdf,.docx"
                            style="display: none;">
                      <button mat-stroked-button 
                              type="button"
                              (click)="fileInput.click()">
                        <mat-icon>swap_horiz</mat-icon>
                        Replace File
                      </button>
                      
                      <button mat-icon-button 
                              type="button"
                              [color]="showDocumentViewer ? 'primary' : ''"
                              (click)="toggleDocumentViewer()"
                              matTooltip="Toggle document viewer">
                        <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                      </button>
                    </div>
                  </div>
                } @else {
                  <!-- No file - show upload area -->
                  <div class="upload-area" 
                      [class.drag-over]="isDragOver"
                      (dragover)="onDragOver($event)"
                      (dragleave)="onDragLeave($event)"
                      (drop)="onDrop($event)">
                    <div class="upload-placeholder">
                      <mat-icon class="upload-icon">cloud_upload</mat-icon>
                      <p>Drag & drop COI file here</p>
                      <span>or</span>
                      <input type="file" #fileInputNew 
                            (change)="onFileSelected($event)"
                            accept=".pdf,.docx"
                            style="display: none;">
                      <button mat-stroked-button 
                              type="button"
                              (click)="fileInputNew.click()">
                        Browse Files
                      </button>
                      <small>PDF or DOCX files only</small>
                    </div>
                  </div>
                }
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Essential Information Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>business</mat-icon>
              Essential Information
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid essential-grid">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Main Document Name</mat-label>
                <input matInput
                       formControlName="mainDocumentName"
                       placeholder="Filled in when you add the COI file"
                       readonly>
                <mat-icon matSuffix>attach_file</mat-icon>
              </mat-form-field>

              
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Party Providing COI <span class="required-indicator">*</span></mat-label>
                <input matInput
                       formControlName="partyProvidingCoi"
                       [matAutocomplete]="partyProvidingAuto"
                       (input)="filterParties($event)"
                       required>
                <mat-autocomplete #partyProvidingAuto="matAutocomplete" [displayWith]="displayPartyFn">
                  @for (party of filteredParties; track party.id) {
                    <mat-option [value]="party">
                      {{ party | partyLabel }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>domain</mat-icon>
                <mat-error *ngIf="form.get('partyProvidingCoi')?.hasError('required')">
                  Party providing COI is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Insured Party</mat-label>
                <input matInput
                       formControlName="insuredParty"
                       [matAutocomplete]="insuredPartyAuto"
                       (input)="filterParties($event)">
                <mat-autocomplete #insuredPartyAuto="matAutocomplete" [displayWith]="displayPartyFn">
                  @for (party of filteredParties; track party.id) {
                    <mat-option [value]="party">
                      {{ party | partyLabel }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>shield</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Insurance Provider (Carrier)</mat-label>
                <input matInput
                       formControlName="insuranceProvider"
                       [matAutocomplete]="insuranceProviderAuto"
                       (input)="filterParties($event)">
                <mat-autocomplete #insuranceProviderAuto="matAutocomplete" [displayWith]="displayPartyFn">
                  @for (party of filteredParties; track party.id) {
                    <mat-option [value]="party">
                      {{ party | partyLabel }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>account_balance</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                  @for (status of statusTypes; track status) {
                    <mat-option [value]="status">{{ status }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>flag</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Coverage Types</mat-label>
                <mat-select formControlName="coverageTypes" multiple>
                  @for (coverage of allCoverageTypes; track coverage) {
                    <mat-option [value]="coverage">{{ coverage }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>security</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Legal Entities</mat-label>
                <input matInput
                       formControlName="legalEntities"
                       [matAutocomplete]="legalEntitiesAuto"
                       (input)="filterLegalEntity($event)">
                <mat-autocomplete #legalEntitiesAuto="matAutocomplete" [displayWith]="displayLegalEntityFn">
                  @for (entity of filteredLegalEntities; track entity.id) {
                    <mat-option [value]="entity">
                      {{ entity.legalEntityName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>account_balance</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Sites</mat-label>
                <mat-select formControlName="sites" multiple>
                  @for (site of filteredSites; track site.siteNum) {
                    <mat-option [value]="site">{{ site.name || site.siteName }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>location_on</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Departments</mat-label>
                <mat-select formControlName="departments" multiple>
                  @for (dept of allDepartments; track dept) {
                    <mat-option [value]="dept">{{ dept }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>business</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Dates Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>schedule</mat-icon>
              Dates
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid dates-grid">
              
              <mat-form-field appearance="outline">
                <mat-label>Effective Date <span class="required-indicator">*</span></mat-label>
                <input matInput 
                       [matDatepicker]="effectiveDatePicker"
                       formControlName="effectiveDate"
                       required>
                <mat-datepicker-toggle matSuffix [for]="effectiveDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #effectiveDatePicker></mat-datepicker>
                <mat-error *ngIf="form.get('effectiveDate')?.hasError('required')">
                  Effective date is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Expiration Date</mat-label>
                <input matInput 
                       [matDatepicker]="expirationDatePicker"
                       formControlName="expirationDate">
                <mat-datepicker-toggle matSuffix [for]="expirationDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #expirationDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Review Date</mat-label>
                <input matInput 
                       [matDatepicker]="lastReviewDatePicker"
                       formControlName="lastReviewDate">
                <mat-datepicker-toggle matSuffix [for]="lastReviewDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #lastReviewDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Next Review Date</mat-label>
                <input matInput 
                       [matDatepicker]="nextReviewDatePicker"
                       formControlName="nextReviewDate">
                <mat-datepicker-toggle matSuffix [for]="nextReviewDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #nextReviewDatePicker></mat-datepicker>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Coordinators & Signatories Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>people</mat-icon>
              Coordinators & Signatories
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid people-grid">
              
              <mat-form-field appearance="outline">
                <mat-label>Internal Coordinator</mat-label>
                <input matInput 
                       formControlName="internalCoordinator"
                       [matAutocomplete]="internalCoordinatorAuto">
                <mat-autocomplete #internalCoordinatorAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Internal Signatory</mat-label>
                <input matInput 
                       formControlName="internalSignatory"
                       [matAutocomplete]="internalSignatoryAuto">
                <mat-autocomplete #internalSignatoryAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>edit</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Primary Owner</mat-label>
                <input matInput
                       formControlName="primaryOwner"
                       [matAutocomplete]="primaryOwnerAuto"
                       (input)="filterUsers($event)">
                <mat-autocomplete #primaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>star</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Secondary Owner</mat-label>
                <input matInput
                       formControlName="secondaryOwner"
                       [matAutocomplete]="secondaryOwnerAuto"
                       (input)="filterUsers($event)">
                <mat-autocomplete #secondaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>star_half</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Tertiary Owner</mat-label>
                <input matInput
                       formControlName="tertiaryOwner"
                       [matAutocomplete]="tertiaryOwnerAuto"
                       (input)="filterUsers($event)">
                <mat-autocomplete #tertiaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>star_outline</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>External Coordinator</mat-label>
                <input matInput
                       formControlName="externalCoordinator"
                       [matAutocomplete]="externalCoordinatorAuto"
                       (input)="filterPeople($event)">
                <mat-autocomplete #externalCoordinatorAuto="matAutocomplete" [displayWith]="displayPersonFn">
                  @for (person of filteredPeople; track person.contactNum) {
                    <mat-option [value]="person">
                      {{ person.fullName || person.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person_pin</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Additional Details Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>more_horiz</mat-icon>
              Additional Details
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">
              
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <textarea matInput 
                          formControlName="description"
                          rows="3"
                          placeholder="COI description and notes">
                </textarea>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Validation Message -->
        @if (!isFormValid()) {
          <mat-card class="validation-card">
            <mat-card-content>
              <div class="validation-message">
                <mat-icon color="warn">warning</mat-icon>
                <span>{{ getValidationMessage() }}</span>
              </div>
            </mat-card-content>
          </mat-card>
        }

      </form>

    </mat-sidenav-content>

    <!-- COI Viewer Sidenav -->
    <mat-sidenav mode="side" 
                 position="end"
                 [opened]="showDocumentViewer"
                 class="document-viewer-sidenav">
      <app-document-viewer-sidenav
        [document]="currentDocument"
        [title]="'COI Viewer'"
        (close)="toggleDocumentViewer()"
        (download)="downloadDocument()"
        (openNewTab)="openDocumentInNewTab()">
      </app-document-viewer-sidenav>
    </mat-sidenav>

  </mat-sidenav-container>
</div>